import React, { FC, useEffect, useMemo, useState, useId } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import 'reactjs-popup/dist/index.css';
import Label from '@shared/uikit/label';
import AsyncSelectForm from '@shared/form/AsyncSelectForm';
import SingleSelectForm from '@shared/form/SingleSelectForm';
import TextForm from '@shared/form/TextForm';
import { CollectionForm, CollectionFormItem } from '@shared/form/CollectionForm';
import { ISelectOption } from '@widgets/multi-select';
import { useClientOptions } from '@app/queries/options/useClientOptions';
import { useManagerOptions } from '@app/queries/options/useManagerOptions';
import { useRegionOptions } from '@app/queries/options/useRegionOptions';
import { useSourceOptions } from '@app/queries/options/useSourceOptions';
import { useTagOptions } from '@app/queries/options/useTagOptions';
import {
  buildAddAnotherClientBody,
  useAddAnotherClient,
} from '@app/queries/account/useAddAnotherClient';
import { useCreateCustomer } from '@app/queries/customer/useCreateCustomer';
import { ACCOUNT_TYPE, AnotherClientApiType, IAnotherClientEntry } from '@entities/account/IAccount';
import { ServerError } from '@app/error/ServerError';
import { resolveAccountTypeTitle } from '@pages/Account/helpers/accountTypeHelper';
import { searchAccounts } from '@app/queries/options/useAccountOptions';
import { Popup, PopupHeader } from '@shared/uikit/popup';
import { BlueTransparentButton } from '@shared/uikit/button';
import { Loader } from '@shared/uikit/loader';
import PermissionGuard from '@shared/security/PermissionGuard';
import { ACCOUNT_PERMISSIONS } from '@app/contstants/permissions';
import { TextInput } from '@shared/uikit/input-text';
import { Tooltip } from 'react-tooltip';

/** Включить, когда бэкенд/UX готовы к сохранению */
const ADD_ANOTHER_CLIENT_SAVE_DISABLED = true;

const DEFAULT_ACCOUNT_ROW = { linkedAccount: null as ISelectOption | null, cashback: '', discount: '' };

const CLIENT_SOURCE_OPTIONS: ISelectOption[] = [
  { value: 'existing', label: 'Существующий клиент' },
  { value: 'new', label: 'Новый клиент' },
];

const TYPE_OPTIONS: ISelectOption[] = [
  { value: 'NONE', label: 'Без типа (NONE)' },
  { value: 'MINING', label: resolveAccountTypeTitle(ACCOUNT_TYPE.MINING) },
  { value: 'REFERRAL', label: resolveAccountTypeTitle(ACCOUNT_TYPE.REFERRAL) },
  { value: 'AGENT', label: resolveAccountTypeTitle(ACCOUNT_TYPE.AGENT) },
];

interface FormValues {
  clientSourceMode: ISelectOption;
  clientId: ISelectOption | null;
  newClientName: string;
  newManagerId: ISelectOption | null;
  newRegionId: ISelectOption | null;
  newSourceId: ISelectOption | null;
  newTagId: ISelectOption | null;
  newClientPercent: string;
  accountType: ISelectOption;
  miningCashback: string;
  miningDiscount: string;
  accountRows: {
    linkedAccount: ISelectOption | null;
    cashback: string;
    discount: string;
  }[];
}

const defaultFormValues = (): FormValues => ({
  clientSourceMode: CLIENT_SOURCE_OPTIONS[0],
  clientId: null,
  newClientName: '',
  newManagerId: null,
  newRegionId: null,
  newSourceId: null,
  newTagId: null,
  newClientPercent: '',
  accountType: TYPE_OPTIONS[0],
  miningCashback: '0',
  miningDiscount: '0',
  accountRows: [{ ...DEFAULT_ACCOUNT_ROW }],
});

export function summarizeAnotherClientEntry(e: IAnotherClientEntry): string {
  const parts = [`client_id: ${e.client_id ?? '—'}`, `type: ${e.type ?? '—'}`];
  if (e.mining_terms) {
    parts.push(
      `mining: ${e.mining_terms.cashback ?? ''} / ${e.mining_terms.discount ?? ''}`
    );
  }
  const nRef = e.referrals?.length ?? 0;
  const nRa = e.referral_account?.length ?? 0;
  if (nRef) parts.push(`referrals: ${nRef}`);
  if (nRa) parts.push(`referral_account: ${nRa}`);
  return parts.join(' · ');
}

/** Компактный список доп. клиентов в шапке карточки аккаунта */
export const AnotherClientsTopBar: FC<{
  items: IAnotherClientEntry[] | null | undefined;
}> = ({ items }) => {
  const list = useMemo(() => (Array.isArray(items) ? items : []), [items]);

  if (list.length === 0) {
    return (
      <span className="text-[11px] text-[#666666] whitespace-nowrap shrink-0">
        Доп. клиенты: нет
      </span>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1 min-w-0 max-w-[min(100%,520px)]">
      <span className="text-[11px] text-[#777777] mr-1 shrink-0">Доп.:</span>
      {list.map((entry, i) => (
        <span
          key={`${entry.client_id ?? 'x'}-${i}`}
          className="text-[11px] px-2 py-0.5 rounded-[6px] bg-[#211F24] border border-solid border-[#2B292F] text-[#CCCCCC] max-w-[200px] truncate"
          title={summarizeAnotherClientEntry(entry)}
        >
          #{entry.client_id ?? '—'} · {entry.type ?? '—'}
        </span>
      ))}
    </div>
  );
};

interface AddAnotherClientPopupProps {
  open: boolean;
  onClose: () => void;
  accountLogin: string;
  onUpdated: () => Promise<void>;
}

const AddAnotherClientPopup: FC<AddAnotherClientPopupProps> = ({
  open,
  onClose,
  accountLogin,
  onUpdated,
}) => {
  const saveAnchorId = `aac-save-${useId().replace(/:/g, '')}`;
  const clientOptionsFetcher = useClientOptions();
  const addFetcher = useAddAnotherClient();
  const createCustomerFetcher = useCreateCustomer();
  const managerOptionsFetcher = useManagerOptions();
  const regionOptionsFetcher = useRegionOptions();
  const sourceOptionsFetcher = useSourceOptions();
  const tagOptionsFetcher = useTagOptions();

  const [defaultClientOptions, setDefaultClientOptions] = useState<ISelectOption[]>([]);
  const [defaultAccountOptions, setDefaultAccountOptions] = useState<ISelectOption[]>([]);
  const [managerOptions, setManagerOptions] = useState<ISelectOption[]>([]);
  const [regionOptions, setRegionOptions] = useState<ISelectOption[]>([]);
  const [sourceOptions, setSourceOptions] = useState<ISelectOption[]>([]);
  const [tagOptions, setTagOptions] = useState<ISelectOption[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    void clientOptionsFetcher.fetchData('').then(setDefaultClientOptions);
  }, []);

  useEffect(() => {
    void searchAccounts('').then(setDefaultAccountOptions);
  }, []);

  useEffect(() => {
    void managerOptionsFetcher.fetchData().then(setManagerOptions);
  }, []);

  useEffect(() => {
    void regionOptionsFetcher.fetchData().then(setRegionOptions);
  }, []);

  useEffect(() => {
    void sourceOptionsFetcher.fetchData().then(setSourceOptions);
  }, []);

  useEffect(() => {
    void tagOptionsFetcher.fetchData().then(setTagOptions);
  }, []);

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: defaultFormValues(),
  });

  useEffect(() => {
    if (!open) return;
    reset(defaultFormValues());
  }, [open, reset]);

  const clientSourceMode = useWatch({ control, name: 'clientSourceMode' });
  const accountType = useWatch({ control, name: 'accountType' });
  const typeValue = accountType?.value as AnotherClientApiType | undefined;
  const isNewClientMode = clientSourceMode?.value === 'new';

  const showMining = typeValue === 'MINING';
  const showAccountRows = typeValue === 'REFERRAL' || typeValue === 'AGENT';

  const {
    fields: rowFields,
    append: appendRow,
    remove: removeRow,
  } = useFieldArray({
    control,
    name: 'accountRows',
  });

  const onSubmit = async (values: FormValues) => {
    if (values.clientSourceMode.value === 'new') {
      if (
        !values.newManagerId ||
        !values.newRegionId ||
        !values.newSourceId ||
        !values.newClientName.trim() ||
        !values.newClientPercent.trim()
      ) {
        toast.error('Заполните данные нового клиента (имя, менеджер, регион, источник, % ViaBTC)');
        return;
      }
    } else {
      if (!values.clientId) {
        toast.error('Выберите клиента');
        return;
      }
      const existingId = Number(values.clientId.value);
      if (Number.isNaN(existingId)) {
        toast.error('Некорректный клиент');
        return;
      }
    }

    setSubmitting(true);
    try {
      let clientId: number;
      if (values.clientSourceMode.value === 'new') {
        const created = await createCustomerFetcher.fetch({
          name: values.newClientName.trim(),
          managers: [values.newManagerId!],
          source: values.newSourceId,
          tag: values.newTagId,
          region: values.newRegionId,
          terms: Number(values.newClientPercent.replace(',', '.')),
        });
        clientId = Number(created.value);
        if (Number.isNaN(clientId)) {
          toast.error('Не удалось получить id созданного клиента');
          return;
        }
      } else {
        clientId = Number(values.clientId!.value);
      }

      const body = buildAddAnotherClientBody({
        clientId,
        type: values.accountType.value as AnotherClientApiType,
        miningCashback: values.miningCashback,
        miningDiscount: values.miningDiscount,
        accountRows: values.accountRows,
      });
      await addFetcher.fetch({ account: accountLogin, body });
      toast.success('Дополнительный клиент сохранён');
      await onUpdated();
      reset(defaultFormValues());
      onClose();
    } catch (e) {
      if (e instanceof ServerError) {
        toast.error(e.error);
      } else if (e instanceof Error) {
        toast.error(e.message);
      } else {
        toast.error('Не удалось сохранить');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formBody = (
    <PermissionGuard permissions={ACCOUNT_PERMISSIONS.EDIT}>
      <div className="grid gap-[14px] w-full max-w-full text-[12px] text-left">
        <div className="grid grid-cols-[140px_1fr] items-center gap-3">
          <Label title="Режим" color="white" />
          <Controller
            name="clientSourceMode"
            control={control}
            render={({ field, fieldState }) => (
              <SingleSelectForm
                placeholder="Режим"
                isLoading={false}
                options={CLIENT_SOURCE_OPTIONS}
                width="100%"
                onChange={(val: ISelectOption | null) =>
                  field.onChange(val ?? CLIENT_SOURCE_OPTIONS[0])
                }
                value={field.value}
                state={fieldState}
              />
            )}
          />
        </div>

        {!isNewClientMode ? (
          <div className="grid grid-cols-[140px_1fr] items-start gap-3">
            <Label title="Клиент" color="white" />
            <div className="w-full">
              <Controller
                name="clientId"
                control={control}
                render={({ field, fieldState }) => (
                  <AsyncSelectForm
                    placeholder="Поиск клиента"
                    isLoading={false}
                    options={defaultClientOptions}
                    loadOptions={(q: string) => clientOptionsFetcher.fetchData(q)}
                    width="100%"
                    onChange={(val: ISelectOption | null) => field.onChange(val)}
                    value={field.value}
                    state={fieldState}
                  />
                )}
              />
              <p className="text-[10px] text-[#666666] mt-1">Список из BI</p>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-[140px_1fr] items-center gap-3">
              <Label title="Имя клиента" color="white" />
              <Controller
                name="newClientName"
                control={control}
                render={({ field, fieldState }) => (
                  <TextInput
                    width="100%"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Введите имя клиента"
                    bgColor="#211F24"
                    borderColor="#211F24"
                  />
                )}
              />
            </div>
            <div className="grid grid-cols-[140px_1fr] items-center gap-3">
              <Label title="Менеджер" color="white" />
              <Controller
                name="newManagerId"
                control={control}
                render={({ field, fieldState }) => (
                  <SingleSelectForm
                    placeholder="Менеджер"
                    isLoading={!managerOptionsFetcher.isSuccess}
                    options={managerOptions}
                    width="100%"
                    onChange={(val: ISelectOption | null) => field.onChange(val)}
                    value={field.value}
                    state={fieldState}
                  />
                )}
              />
            </div>
            <div className="grid grid-cols-[140px_1fr] items-center gap-3">
              <Label title="Регион" color="white" />
              <Controller
                name="newRegionId"
                control={control}
                render={({ field, fieldState }) => (
                  <SingleSelectForm
                    placeholder="Регион"
                    isLoading={!regionOptionsFetcher.isSuccess}
                    options={regionOptions}
                    width="100%"
                    onChange={(val: ISelectOption | null) => field.onChange(val)}
                    value={field.value}
                    state={fieldState}
                  />
                )}
              />
            </div>
            <div className="grid grid-cols-[140px_1fr] items-center gap-3">
              <Label title="Источник" color="white" />
              <Controller
                name="newSourceId"
                control={control}
                render={({ field, fieldState }) => (
                  <SingleSelectForm
                    placeholder="Источник"
                    isLoading={!sourceOptionsFetcher.isSuccess}
                    options={sourceOptions}
                    width="100%"
                    isCreatable={true}
                    onChange={(val: ISelectOption | null) => field.onChange(val)}
                    value={field.value}
                    state={fieldState}
                  />
                )}
              />
            </div>
            <div className="grid grid-cols-[140px_1fr] items-center gap-3">
              <Label title="Тег" color="white" />
              <Controller
                name="newTagId"
                control={control}
                render={({ field, fieldState }) => (
                  <SingleSelectForm
                    placeholder="Тег"
                    isLoading={!tagOptionsFetcher.isSuccess}
                    options={tagOptions}
                    width="100%"
                    isCreatable={true}
                    onChange={(val: ISelectOption | null) => field.onChange(val)}
                    value={field.value}
                    state={fieldState}
                  />
                )}
              />
            </div>
            <div className="grid grid-cols-[140px_1fr] items-center gap-3">
              <Label title="Условия на ViaBTC, %" color="white" />
              <div className="flex items-center gap-2 text-white">
                <Controller
                  name="newClientPercent"
                  control={control}
                  render={({ field, fieldState }) => (
                    <div className="w-[80px]">
                      <TextForm
                        value={field.value}
                        onChange={field.onChange}
                        state={fieldState}
                        type="number"
                      />
                    </div>
                  )}
                />
                <span>%</span>
              </div>
            </div>
          </>
        )}

        <div className="grid grid-cols-[140px_1fr] items-start gap-3">
          <Label title="Тип аккаунта" color="white" />
          <div className="w-full">
            <Controller
              name="accountType"
              control={control}
              render={({ field, fieldState }) => (
                <SingleSelectForm
                  placeholder="Тип"
                  isLoading={false}
                  options={TYPE_OPTIONS}
                  width="100%"
                  onChange={(val: ISelectOption | null) =>
                    field.onChange(val ?? TYPE_OPTIONS[0])
                  }
                  value={field.value}
                  state={fieldState}
                />
              )}
            />
            <p className="text-[10px] text-[#666666] mt-1">
              Для реферального и агентского типа ниже укажите привязку к ViaBTC-аккаунтам (поле в API:
              referral_account / referrals).
            </p>
          </div>
        </div>

        {showMining ? (
          <>
            <div className="grid grid-cols-[140px_1fr] items-center gap-3">
              <Label title="Кэшбэк (mining_terms)" color="white" />
              <Controller
                name="miningCashback"
                control={control}
                render={({ field, fieldState }) => (
                  <div className="w-[120px]">
                    <TextForm
                      value={field.value}
                      onChange={field.onChange}
                      state={fieldState}
                      type="number"
                    />
                  </div>
                )}
              />
            </div>
            <div className="grid grid-cols-[140px_1fr] items-center gap-3">
              <Label title="Скидка (mining_terms)" color="white" />
              <Controller
                name="miningDiscount"
                control={control}
                render={({ field, fieldState }) => (
                  <div className="w-[120px]">
                    <TextForm
                      value={field.value}
                      onChange={field.onChange}
                      state={fieldState}
                      type="number"
                    />
                  </div>
                )}
              />
            </div>
          </>
        ) : null}

        {showAccountRows ? (
          <div className="space-y-2">
            {typeValue === 'REFERRAL' ? (
              <p className="text-[11px] text-[#959595]">
                Реферальный: укажите агентский ViaBTC-аккаунт, к которому привязана эта связь
                (referral_account).
              </p>
            ) : (
              <p className="text-[11px] text-[#959595]">
                Агентский: перечислите реферальные ViaBTC-аккаунты (referrals).
              </p>
            )}
            <p className="text-[10px] text-[#666666]">Поиск аккаунтов в BI</p>
            <CollectionForm>
              {rowFields.map((rowField, index) => (
                <div
                  key={rowField.id}
                  className="pt-[12px] border-t border-solid border-opacity-10 border-[#959595]"
                >
                  <CollectionFormItem
                    onCreate={() => appendRow({ ...DEFAULT_ACCOUNT_ROW })}
                    onRemove={() => removeRow(index)}
                    index={index}
                  >
                    <div className="w-full grid grid-cols-[140px_1fr] gap-y-2 gap-x-3 items-center">
                      <Label
                        title={
                          typeValue === 'REFERRAL' ? 'Аккаунт привязки' : 'Реф. аккаунт'
                        }
                        color="white"
                      />
                      <Controller
                        name={`accountRows.${index}.linkedAccount`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <AsyncSelectForm
                            placeholder="Поиск аккаунта"
                            isLoading={false}
                            options={defaultAccountOptions}
                            loadOptions={(q: string) => searchAccounts(q)}
                            width="100%"
                            onChange={(val: ISelectOption | null) => field.onChange(val)}
                            value={field.value}
                            state={fieldState}
                          />
                        )}
                        rules={{ required: showAccountRows }}
                      />
                      <Label title="Кэшбэк %" color="white" />
                      <Controller
                        name={`accountRows.${index}.cashback`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <div className="w-[100px]">
                            <TextForm
                              value={field.value}
                              onChange={field.onChange}
                              state={fieldState}
                              type="number"
                            />
                          </div>
                        )}
                      />
                      <Label title="Скидка %" color="white" />
                      <Controller
                        name={`accountRows.${index}.discount`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <div className="w-[100px]">
                            <TextForm
                              value={field.value}
                              onChange={field.onChange}
                              state={fieldState}
                              type="number"
                            />
                          </div>
                        )}
                      />
                    </div>
                  </CollectionFormItem>
                </div>
              ))}
            </CollectionForm>
          </div>
        ) : null}

        <div className="pt-2 max-w-[240px]">
          <div
            id={saveAnchorId}
            className={
              ADD_ANOTHER_CLIENT_SAVE_DISABLED
                ? 'cursor-not-allowed rounded-[8px] opacity-50'
                : undefined
            }
            onClick={
              ADD_ANOTHER_CLIENT_SAVE_DISABLED
                ? () =>
                    toast.info(
                      'Сохранение дополнительного клиента пока в разработке (in progress)'
                    )
                : undefined
            }
          >
            <div className={ADD_ANOTHER_CLIENT_SAVE_DISABLED ? 'pointer-events-none' : undefined}>
              <BlueTransparentButton
                title={
                  submitting || addFetcher.isPending ? (
                    <Loader
                      size={24}
                      position="center"
                      loading={submitting || addFetcher.isPending}
                    />
                  ) : (
                    'Сохранить доп. клиента'
                  )
                }
                onClick={() => {
                  if (ADD_ANOTHER_CLIENT_SAVE_DISABLED) return;
                  if (submitting || addFetcher.isPending) return;
                  void handleSubmit(onSubmit)();
                }}
              />
            </div>
          </div>
          {ADD_ANOTHER_CLIENT_SAVE_DISABLED ? (
            <Tooltip
              anchorSelect={`#${saveAnchorId}`}
              content="Пока в разработке (in progress)"
            />
          ) : null}
        </div>
      </div>
    </PermissionGuard>
  );

  return (
    <Popup open={open} width="560px" padding="16px" onClose={onClose}>
      <div className="text-left w-full">
        <PopupHeader title="Дополнительные клиенты" onClose={onClose} />
        <div className="max-h-[min(75vh,640px)] overflow-y-auto overflow-x-hidden py-[16px] pr-1">
          {formBody}
        </div>
      </div>
    </Popup>
  );
};

export default AddAnotherClientPopup;
